import {
  applyHomography,
  clamp,
  CROP_MARGIN,
  keystoneOverhang,
  MAX_KEYSTONE,
  windowHomography,
  type SourceWindow,
  type ViewRect,
} from "../shared/geometry";

/**
 * The user's window into the sensor frame: digital pan, zoom, free rotation, and a
 * desk-view keystone.
 *
 * View state, and only view state. Gestures move it, the overlay draws it, and the
 * scanner is *told* the result — every window this produces goes to the scanner as a
 * `framing`, so what the user frames is exactly what the detector sees. Zooming is
 * digital on purpose: `applyConstraints({ zoom })` exists on some Android devices and
 * nowhere else, and a sensor-side crop and a source-side window are indistinguishable to
 * every stage downstream. Detection genuinely gains from zooming in — the 640 letterbox
 * raster stops being spent on scenery — and the warp loses nothing, because it always
 * samples the full-resolution frame through the quad.
 *
 * At zoom 1 the window is the largest viewport-aspect rectangle that fits inside the
 * sensor frame at the current angle; the keystone trapezoid lies inside that rectangle,
 * so the same clamp keeps it inside the frame. That is what keeps the corners free of
 * missing pixels as the sliders move: the view pulls in a little instead of showing
 * black.
 *
 * The detection area is the one thing here the preview does *not* sample through: a
 * centred sub-rectangle of the view, which the detector is letterboxed from while the
 * preview keeps showing the whole window and outlines the area on it. That asymmetry is
 * the feature — cropping the preview too would just be zoom, whereas the point is to
 * tell the scanner "only look here" while the user keeps their bearings. It buys the
 * same thing zoom buys the detector (the model's raster stops being spent on scenery)
 * without moving the camera, and it keeps a second card at the edge of the table out of
 * the answer. Held as fractions of the *view*, so it stays put on screen while pan, zoom
 * and rotation move the frame underneath it.
 *
 * State is a zoom factor, an angle, a keystone strength, a detection-area size and a
 * centre in sensor-frame fractions — the centre deliberately does not move when the
 * angle does, so the slider turns the image about the point being looked at. Gestures
 * arrive as fractions of the on-screen view; converting them into frame pixels needs the
 * geometry of the last window computed, which `window` caches for that purpose — before
 * the first frame, gestures are no-ops rather than guesses.
 */

/** Past ~6x of an already cover-cropped 4K frame the crop is too soft to read anyway. */
const MAX_ZOOM = 6;
/**
 * How far either axis of the detection area may be pulled in.
 *
 * A quarter of the view is already a tight aiming window — the card has to fit inside it
 * to be detected at all — and below that the letterbox starts upscaling a region smaller
 * than the model's own raster, which buys nothing.
 */
export const MIN_CROP = 0.25;
/**
 * The detection area the app starts with, as fractions of the view.
 *
 * A card held up to a phone in portrait is a tall rectangle in the middle of the screen,
 * so the default area is one too: nearly the full width, three quarters of the height.
 * It is a default rather than the whole view because the scenery a phone catches above
 * and below the card — the rest of the desk, the user's lap — is where second cards and
 * card-shaped distractors live, and spending the model's 640 raster on it buys nothing.
 */
export const DEFAULT_CROP_W = 0.95;
export const DEFAULT_CROP_H = 0.75;

export class ViewControl {
  private level = 1;
  /** Clockwise on-screen rotation of the image, degrees. */
  private angle = 0;
  /** Desk-view strength: the fraction of the top edge inset at each corner. */
  private trapezoid = 0;
  /**
   * Desk-view vertical scale. The keystone is only determined up to a global vertical
   * stretch — that residual depends on the camera's focal length, which nothing here
   * knows — so without compensation the corrected image shows every desk shape
   * uniformly squashed or stretched in y. This factor is how much *more* desk the
   * window samples vertically than the viewport aspect asks for; the calibrator
   * measures it from accepted cards, whose true aspect is known.
   */
  private stretch = 1;
  /**
   * The detection area, as fractions of the view's width and height — the size that is
   * *drawn*, which the detector is then shown with `CROP_MARGIN` added per side. The two
   * axes are independent: the handle that sets them is one corner, but pulling it left
   * crops both sides and pulling it up crops both ends, so the area stays centred on
   * what the user is aiming at.
   */
  private cropWidth = DEFAULT_CROP_W;
  private cropHeight = DEFAULT_CROP_H;
  /** Centre of the window, in fractions of the sensor frame. */
  private centreX = 0.5;
  private centreY = 0.5;
  /** Geometry of the last window, for converting gesture fractions into sensor pixels. */
  private last: { vw: number; vh: number; window: SourceWindow } | null = null;

  get zoom(): number {
    return this.level;
  }

  /** Degrees of clockwise display rotation, as the slider set them. */
  get rotation(): number {
    return this.angle;
  }

  /** Desk-view strength, -1..1 of MAX_KEYSTONE — what the slider shows, sign included. */
  get deskView(): number {
    return this.trapezoid / MAX_KEYSTONE;
  }

  /**
   * Whether pan and zoom are untouched. Rotation, desk view and the detection area are
   * deliberately not part of this: someone whose camera hangs at an angle set them once,
   * and a double tap to shed an accidental zoom must not undo their setup. Each of those
   * has its own way back — the sliders, and the area's own chip.
   */
  get isDefault(): boolean {
    return (
      this.level === 1 && Math.abs(this.centreX - 0.5) < 1e-3 && Math.abs(this.centreY - 0.5) < 1e-3
    );
  }

  reset(): void {
    this.level = 1;
    this.centreX = 0.5;
    this.centreY = 0.5;
  }

  /**
   * The detection area's size as drawn and reported, in fractions of the view. Both 1
   * when uncropped; what the detector is shown is this plus `CROP_MARGIN` per side.
   */
  get crop(): { w: number; h: number } {
    return { w: this.cropWidth, h: this.cropHeight };
  }

  /** Whether an area is being framed at all — i.e. whether there is an outline to draw. */
  get isCropped(): boolean {
    return this.cropWidth < 0.999 || this.cropHeight < 0.999;
  }

  /** Whether the area is where it started, which is what the chip's ⟲ leads back to. */
  get isCropDefault(): boolean {
    return (
      Math.abs(this.cropWidth - DEFAULT_CROP_W) < 1e-3 &&
      Math.abs(this.cropHeight - DEFAULT_CROP_H) < 1e-3
    );
  }

  /**
   * Size the detection area, in fractions of the view. Clamped rather than rejected, so
   * a finger dragged past the limit parks the handle on it instead of losing the drag.
   */
  setCrop(width: number, height: number): void {
    this.cropWidth = clamp(width, MIN_CROP, 1);
    this.cropHeight = clamp(height, MIN_CROP, 1);
  }

  resetCrop(): void {
    this.cropWidth = DEFAULT_CROP_W;
    this.cropHeight = DEFAULT_CROP_H;
  }

  /** A centred sub-rectangle of `window`, given its size in fractions of the view. */
  private centred(window: SourceWindow, fracW: number, fracH: number): ViewRect {
    const w = window.sw * Math.min(1, fracW);
    const h = window.keystoneSpan * Math.min(1, fracH);
    return { x: (window.sw - w) / 2, y: (window.keystoneSpan - h) / 2, w, h };
  }

  /**
   * The detection area as *drawn* inside `window`, in view pixels: centred, and the whole
   * window when uncropped. The preview's outline reads this — it is the rectangle the
   * user is promised, and the one the handle and the chip both talk about.
   */
  frameRect(window: SourceWindow): ViewRect {
    return this.centred(window, this.cropWidth, this.cropHeight);
  }

  /**
   * The region the detector is actually letterboxed from: `frameRect` grown by
   * `CROP_MARGIN` per side, clamped to the window. The two differ on purpose — see
   * CROP_MARGIN — so a card lined up flush against the outline still has its corners
   * inside the raster rather than on its border.
   */
  detectRect(window: SourceWindow): ViewRect {
    return this.centred(
      window,
      this.cropWidth + 2 * CROP_MARGIN,
      this.cropHeight + 2 * CROP_MARGIN,
    );
  }

  /** Turn the displayed image to `degrees` clockwise. The centre stays put. */
  setRotation(degrees: number): void {
    this.angle = degrees;
  }

  /**
   * Set desk view to `strength` of the maximum keystone, -1..1. Positive undoes a
   * camera looking down across the desk; negative, one looking up at it.
   */
  setDeskView(strength: number): void {
    this.trapezoid = clamp(strength, -1, 1) * MAX_KEYSTONE;
    // Flat view, flat scale: the stretch is a correction *of the keystone's* residual
    // and has no meaning without one.
    if (this.trapezoid === 0) this.stretch = 1;
  }

  /** Set the desk-view vertical scale — normally the calibrator's, not a hand's. */
  setDeskStretch(factor: number): void {
    this.stretch = clamp(factor, 0.5, 2);
  }

  /**
   * The visible window, in sensor pixels: the largest viewport-aspect rectangle that
   * fits the sensor frame at the current angle, divided by zoom, centred on the pan
   * target, its rotated bounding box clamped inside the frame. The keystone trapezoid
   * is a subset of that rectangle, so the clamp covers it too. The centre is
   * re-anchored to the clamped position so panning against an edge never banks up
   * travel a later drag must first unwind.
   */
  window(vw: number, vh: number, viewW: number, viewH: number): SourceWindow {
    if (!vw || !vh || viewW <= 0 || viewH <= 0) {
      return { sw: vw, sh: vh, affine: [1, 0, 0, 0, 1, 0], keystone: 0, keystoneSpan: vh };
    }
    // The image turns clockwise by `angle` on screen, so window axes map into the
    // sensor through the opposite turn: R = [a, b; -b, a] with a = cos, b = sin.
    const rad = (this.angle * Math.PI) / 180;
    const a = Math.cos(rad);
    const b = Math.sin(rad);
    const absA = Math.abs(a);
    const absB = Math.abs(b);
    // The stretch samples a taller (or shorter) strip of desk into the same viewport,
    // which is exactly a global vertical scale of the displayed image — and because it
    // is folded into the window's own aspect, every consumer inherits it unchanged.
    const aspect = viewW / viewH / this.stretch;
    // The centred keystone samples past the rect's sides at its magnified edge, so the
    // region that must fit and stay inside the sensor is the rect widened by the
    // overhang — not the rect itself.
    const spread = 1 + 2 * keystoneOverhang(this.trapezoid);
    // Largest inscribed window: its bounding box (w|cos| + h|sin| across, the mirror
    // image down) must fit the frame on both axes.
    const fitAspect = aspect * spread;
    const h0 = Math.min(vw / (fitAspect * absA + absB), vh / (fitAspect * absB + absA));
    const sh = h0 / this.level;
    const sw = aspect * sh;
    const halfW = (sw * spread * absA + sh * absB) / 2;
    const halfH = (sw * spread * absB + sh * absA) / 2;
    const cx = clamp(this.centreX * vw, halfW, vw - halfW);
    const cy = clamp(this.centreY * vh, halfH, vh - halfH);
    this.centreX = cx / vw;
    this.centreY = cy / vh;
    const tx = cx - (a * sw + b * sh) / 2;
    const ty = cy - (-b * sw + a * sh) / 2;
    const window: SourceWindow = {
      sw,
      sh,
      affine: [a, b, tx, -b, a, ty],
      keystone: this.trapezoid,
      keystoneSpan: sh,
    };
    this.last = { vw, vh, window };
    return window;
  }

  /** Pan by fractions of the current view — dragging one full view width pans one width. */
  panBy(fracX: number, fracY: number): void {
    if (!this.last) return;
    const { vw, vh, window } = this.last;
    // Through the affine only: under the keystone a drag's true footprint varies down
    // the screen, but pan is a closed loop through the user's eyes and hand, and the
    // flat direction is right — only the rate is locally approximate.
    const [a, b, , c, d] = window.affine;
    const dx = fracX * window.sw;
    const dy = fracY * window.sh;
    this.centreX = clamp(this.centreX + (a * dx + b * dy) / vw, 0, 1);
    this.centreY = clamp(this.centreY + (c * dx + d * dy) / vh, 0, 1);
  }

  /** Zoom by `factor`, keeping the frame point under (anchorX, anchorY) — view fractions — still. */
  zoomBy(factor: number, anchorX = 0.5, anchorY = 0.5): void {
    if (!this.last || !(factor > 0)) return;
    const { vw, vh, window } = this.last;
    const next = clamp(this.level * factor, 1, MAX_ZOOM);
    const applied = next / this.level;
    this.level = next;
    if (applied === 1) return;
    // The sensor point under the anchor — through the full map, keystone included —
    // must sit at the same view fraction of the smaller window, so the new centre is
    // that point pushed half a window inward along the flat axes.
    const h = windowHomography(window);
    const [px, py] = applyHomography(h, anchorX * window.sw, anchorY * window.sh);
    const [a, b, , c, d] = window.affine;
    const ox = (0.5 - anchorX) * (window.sw / applied);
    const oy = (0.5 - anchorY) * (window.sh / applied);
    this.centreX = clamp((px + a * ox + b * oy) / vw, 0, 1);
    this.centreY = clamp((py + c * ox + d * oy) / vh, 0, 1);
  }
}
